import React from "react";
import { GreyCard } from "./UI";

const processSteps = [
  { title: "Ticket Creation", text: "Your issue is logged in our internal system. You'll receive a confirmation message or Ticket ID for tracking." },
  { title: "Review and Categorization", text: "Support agents analyze your issue and classify it as: Technical, Account Related, Verification, or General Help." },
  { title: "Technical Escalation (If Required)", text: "If your issue involves system bugs, login errors, or upload failures, it is escalated to the Development Team for deeper investigation." },
  { title: "Resolution and Testing", text: "The issue is resolved and tested internally to ensure everything works properly before updating your account." },
  { title: "User Notification", text: "You'll be informed that the issue is fixed, along with any further instructions or preventive advice." },
];

export default function SupportContact() {
  return (
    <div className="sz-page-section" id="support">
      <div className="container">
        <h2 className="sz-plain-title mb-1">4. Helpline and Support: How to Get Help</h2>
        <p>At Selectezi, customer support ranks very high in our priorities.</p>
        <p>
          We understand that users may experience technical glitches, login
          issues, or confusion regarding platform features, and our Support
          and Helpline Team is always ready to assist you.
        </p>
        <p className="mb-4">
          Whether it's a signup problem, profile verification, timeline post
          rejection, or system error, you can reach us easily through the
          official channels listed below.
        </p>

        <div className="sz-contact-card mb-4">
          <h3>
            <span aria-hidden="true">✉</span> Contact Options
          </h3>
          <p className="fw-bold mb-2">Email Support</p>
          <p className="mb-2">
            For all general queries, account problems, or technical issues,
            email us at:
          </p>
          <div className="sz-email-highlight">
            <a href="mailto:support@selectezi.com">support@selectezi.com</a>
          </div>
          <p className="fw-semibold mb-1">When sending an email, always include:</p>
          <ul className="sz-bullet-list">
            <li>Your Full Name and Registered Email</li>
            <li>Your Business Name (if applicable)</li>
            <li>A Short Description of the issue</li>
            <li>Screenshots (if possible) showing the problem</li>
            <li>Device or Browser you're using (e.g., Android / iPhone / Chrome / Edge)</li>
          </ul>
          <p className="fst-italic mb-4">
            Our support team carefully reviews every ticket and responds with
            the most accurate and effective solution.
          </p>

          <hr className="sz-divider mb-3" />
          <p className="fw-bold text-primary-sz mb-2">Support Hours</p>
          <p>Monday to Saturday, 9:00 AM to 6:00 PM (Pakistan Standard Time)</p>
          <p className="mb-0">
            Tickets received outside working hours will be attended to on the
            next working day. During high traffic periods, responses may take
            longer, but all user requests are logged and prioritized.
          </p>
        </div>

        <h4 className="fw-bold mt-5 mb-3">Help Options</h4>
        <p className="mb-3">
          Selectezi also offers built-in tools to report issues directly, no
          need to leave the site.
        </p>
        <div className="row g-3 mb-5">
          <div className="col-md-6">
            <div className="sz-grey-card">
              <span className="sz-pill-badge">"Report to Selectezi" Button</span>
              <p className="mb-0 mt-2">
                Click to open the support form, choose your issue type,
                describe the problem, and submit.
              </p>
            </div>
          </div>
          <div className="col-md-6">
            <div className="sz-grey-card">
              <span className="sz-pill-badge">"Report Problem" Button</span>
              <p className="mb-0 mt-2">
                Use this for technical glitches, feature errors, or content
                problems.
              </p>
              <p className="mb-0 mt-2 text-muted small">
                Your report automatically includes system information
                (browser, version, error code), helping our team identify and
                fix the issue faster.
              </p>
            </div>
          </div>
        </div>

        <h4 className="fw-bold mb-3">How the Support Process Works</h4>
        <p className="mb-4">
          Each time you contact Selectezi Support, by Email or In-App Help,
          your request follows these stages:
        </p>
        <div className="row">
          <div className="col-lg-8">
            {processSteps.map((s, i) => (
              <div className="sz-process-step" key={i}>
                <span className="sz-process-num-light">{i + 1}</span>
                <div>
                  <h6>{s.title}</h6>
                  <p className="mb-0">{s.text}</p>
                </div>
              </div>
            ))}
            <div className="sz-grey-card-avg mt-3">
              <p className="fw-bold mb-1">Average Resolution Time:</p>
              <p className="mb-0">Minor issues: 12 to 24 hours</p>
              <p className="mb-0">Technical/system issues: 24 to 48 hours</p>
            </div>
          </div>
        </div>

        <div className="row g-3 mt-4">
          <div className="col-md-6" >
            <GreyCard title="Before You Contact Support — Quick Self-Check" >
              <p>Please try these quick fixes before submitting a ticket.</p>
              <ul className="sz-bullet-list mb-0">
                <li>Refresh the page or restart the app</li>
                <li>Clear browser cache/cookies</li>
                <li>Switch network (Wi-Fi to Mobile Data)</li>
                <li>Try from another browser or device</li>
                <li>Ensure a stable internet connection</li>
              </ul>
            </GreyCard>
          </div>
          <div className="col-md-6">
            <GreyCard title="Tips for Faster Help">
              <p>To help us resolve your issue quickly and accurately, please:</p>
              <ul className="sz-bullet-list mb-0">
                <li>Attach clear screenshots or recordings if available</li>
                <li>Mention your device and browser (e.g., Samsung A52 / Chrome 120)</li>
                <li>Explain exactly what you were doing when the issue occurred (e.g., while uploading image, while clicking "create ad")</li>
                <li>Specify your account type (Retailer, Wholesaler, Manufacturer, etc.)</li>
                <li>Be polite and professional, all conversations are stored for quality purposes</li>
              </ul>
            </GreyCard>
          </div>
        </div>
        <p className="mt-3">
          If the issue persists even after these steps, contact support right
          away.
        </p>

        <div className="sz-info-box mt-4">
          <p className="fw-bold mb-2">Our Commitment</p>
          <p className="mb-1">The Selectezi Support and Helpline Team is dedicated to:</p>
          <ul className="mb-0">
            <li>Providing quick, precise, and expert help</li>
            <li>Protecting user privacy and data confidentiality</li>
            <li>Resolving all technical and account issues efficiently</li>
            <li>Continuously improving the platform based on user feedback</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
